import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { returnSalesAPI } from "../../SalesService";
import { calculateItemTax, buildInvoicePayloadWithTax, calculateCartTotals } from "../../NewSales/utils/taxCalculations";
import { getDefaultCurrency } from "../../NewSales/utils/salesCalculations";
import { determineTaxType } from "../../NewSales/hooks/usePaymentManager";

const INITIAL_PAYMENT_DATA = (pharmacyPOS) => ({
    currency: getDefaultCurrency(pharmacyPOS),
    type: "cash",
    amount: "",
    patientName: "",
    partialPaid: null,
    isSettle: false,
    notes: "",
    reason: "", // Added for returns
    zigConversionValue: 1,
});

export const useReturnPaymentManager = ({
    t,
    cart,
    itemTaxes,
    discountType,
    discountValue,
    transportCost,
    customerType,
    selectedPatient,
    exchangeRate,
    pharmacyPOS,
    getCurrencySymbol,
    setLoading,
    parentSalesId,
    convertCurrency,
    currency,
    posTaxType,
    pharmacyStateCode,
    patientStateCode
}) => {
    const [paymentData, setPaymentData] = useState(
        INITIAL_PAYMENT_DATA(pharmacyPOS)
    );
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showInvoice, setShowInvoice] = useState(false);
    const [invoiceData, setInvoiceData] = useState(null);
    const [errors, setErrors] = useState({});

    // Determine if we should use intra-state (SGST/CGST) or inter-state (IGST)
    const isSameState = useMemo(() => {
        return determineTaxType(pharmacyStateCode, patientStateCode);
    }, [pharmacyStateCode, patientStateCode]);

    // Calculate totals using the tax calculations (for returns)
    const totals = useMemo(() => {
        // Create a virtual cart for calculation where quantity is returnQuantity
        const returnCart = cart
            .filter(item => (item.returnQuantity || 0) > 0)
            .map(item => ({
                ...item,
                quantity: item.returnQuantity
            }));

        // Prepare returnItemTaxes with proportional values.
        const proportionalTaxes = {};
        cart.forEach(item => {
            if (!(item.returnQuantity > 0)) return;
            const itemKey = `${item._id}-${item.selectedBatchId}`;
            const taxInfo = itemTaxes[itemKey];
            if (!taxInfo) return;

            const proportion = item.returnQuantity / (item.saleQuantity || item.quantity || 1);

            proportionalTaxes[itemKey] = {
                ...taxInfo,
                taxableAmount: taxInfo.taxableAmount * proportion,
                tax: taxInfo.tax * proportion,
                CGST: taxInfo.CGST * proportion,
                SGST: taxInfo.SGST * proportion,
                IGST: taxInfo.IGST * proportion,
                VAT: taxInfo.VAT * proportion
            };
        });

        return calculateCartTotals(
            returnCart,
            proportionalTaxes,
            discountType,
            discountValue,
            transportCost,
            posTaxType,
            isSameState
        );
    }, [cart, itemTaxes, discountType, discountValue, transportCost, posTaxType, isSameState]);

    const openPaymentModal = useCallback(
        ({ customerName, amount }) => {
            setPaymentData((prev) => ({
                ...prev,
                currency: getDefaultCurrency(pharmacyPOS),
                patientName: customerName || prev.patientName,
                amount: amount?.toFixed(3) ?? "",
            }));
            setErrors({});
            setShowPaymentModal(true);
        },
        [pharmacyPOS]
    );

    const closePaymentModal = useCallback(() => {
        setShowPaymentModal(false);
    }, []);

    const resetPaymentState = useCallback(() => {
        setPaymentData(INITIAL_PAYMENT_DATA(pharmacyPOS));
        setErrors({});
    }, [pharmacyPOS]);

    const handlePaymentSave = useCallback(async () => {
        const validationErrors = {};
        if (!paymentData.type) validationErrors.type = t("Payment type is required");
        if (!paymentData.amount) validationErrors.amount = t("Amount is required");

        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) return null;

        const convertedNetTotal = convertCurrency(totals.netTotal, currency, paymentData.currency).toFixed(2);
        // use return Math.ceil(converted * 100) / 100;
        const netTotal = Math.ceil(convertedNetTotal * 100) / 100;

        // Bill-level discount rate so that return taxes are based on
        // taxableAmountAfterDiscount (proportional to returned quantities).
        const billDiscountRate =
            totals.subtotal > 0 ? (totals.discount || 0) / totals.subtotal : 0;
        // Build return payload with tax information
        const items = cart
            .filter(item => (item.returnQuantity || 0) > 0)
            .map((item) => {
                const itemKey = `${item._id}-${item.selectedBatchId}`;
                const taxInfo = itemTaxes[itemKey] || {};
                const proportion = (item.returnQuantity || 0) / (item.quantity || 1);

                const taxableBeforeDiscount =
                    (taxInfo.taxableAmount || 0) * (item.returnQuantity > 0 ? proportion : 0);
                const taxableAfterDiscount =
                    taxableBeforeDiscount - taxableBeforeDiscount * billDiscountRate;

                const taxRate = taxInfo.taxPercentage || 0;

                let itemTax = 0;
                let itemCGST = 0;
                let itemSGST = 0;
                let itemIGST = 0;
                let itemVAT = 0;

                // Determine item-specific tax percentages based on isSameState
                let itemIGSTPercentage = 0;
                let itemCGSTPercentage = 0;
                let itemSGSTPercentage = 0;

                if (posTaxType === "gst") {
                    if (!isSameState) {
                        itemTax = (taxableAfterDiscount * taxRate) / 100;
                        itemIGST = itemTax;
                        itemIGSTPercentage = taxRate;
                    } else {
                        itemTax = (taxableAfterDiscount * taxRate) / 100;
                        itemCGST = itemTax / 2;
                        itemSGST = itemTax / 2;
                        itemCGSTPercentage = taxRate / 2;
                        itemSGSTPercentage = taxRate / 2;
                    }
                } else if (posTaxType === "vat") {
                    itemVAT = (taxableAfterDiscount * taxRate) / 100;
                    itemTax = itemVAT;
                }

                return {
                    medicineQunatity: item.returnQuantity || 0,
                    medicineName: item.saltName,
                    manufacturer: item.manufacturer,
                    brandName: item.brandName,
                    stockId: item.selectedBatchId,
                    itemId: item._id,
                    batchNumber: item.batchNumber,
                    productForm: item.productForm,
                    isOTC: item.isOTC,
                    // Tax information for return
                    taxPercentage: taxInfo.taxPercentage || 0,
                    tax: itemTax.toFixed(2),
                    CGST: itemCGST.toFixed(2),
                    SGST: itemSGST.toFixed(2),
                    IGST: itemIGST.toFixed(2),
                    VAT: itemVAT.toFixed(2),
                    isIGST: !isSameState && posTaxType === "gst",
                    IGSTPercentage: itemIGSTPercentage.toFixed(2),
                    CGSTPercentage: itemCGSTPercentage.toFixed(2),
                    SGSTPercentage: itemSGSTPercentage.toFixed(2)
                };
            });

        // Calculate tax percentages based on isSameState
        let IGSTPercentage = 0;
        let CGSTPercentage = 0;
        let SGSTPercentage = 0;

        const maxTaxPercentage = Math.max(...Object.values(itemTaxes || {}).map(t => t.taxPercentage || 0), 0);

        if (posTaxType === "gst") {
            if (!isSameState) {
                IGSTPercentage = maxTaxPercentage;
            } else {
                CGSTPercentage = maxTaxPercentage / 2;
                SGSTPercentage = maxTaxPercentage / 2;
            }
        }

        const payload = {
            clinicId: pharmacyPOS?.hospital_id,
            clinicName: pharmacyPOS?.hospital?.name || "",
            pharmacyId: pharmacyPOS?._id,
            pharmacyName: pharmacyPOS?.facility_name,
            patientId: customerType !== "walking" ? selectedPatient?._id : null,
            isHospitalConnected: customerType === 'hospital_patient',
            patientName: paymentData.patientName || selectedPatient?.name || t("Walk-in Customer"),
            parentSalesId: parentSalesId,
            discountType: discountType.toLowerCase(),
            discount: discountValue || 0,
            transportCost: transportCost || 0,
            netTotal,
            otherNetTotalPrice: Number(netTotal * exchangeRate).toFixed(2),
            currencyType: paymentData.currency || "INR",
            paymentType: paymentData.type,
            isConversion: paymentData.currency !== "INR",
            customerType: customerType,
            totalPaid: Number(paymentData.amount),
            isPartialPaid: false,
            isPaid: true,
            zigConversionValue: exchangeRate,
            isSettled: true,
            isReturn: true,
            reason: paymentData.reason,

            // Tax fields at invoice level - UPDATED with isSameState
            isIGST: !isSameState && posTaxType === "gst",
            IGST: totals.IGST.toFixed(2),
            taxPercentage: maxTaxPercentage,
            tax: totals.tax.toFixed(2),
            SGST: totals.SGST.toFixed(2),
            CGST: totals.CGST.toFixed(2),
            IGSTPercentage: IGSTPercentage.toFixed(2),
            CGSTPercentage: CGSTPercentage.toFixed(2),
            SGSTPercentage: SGSTPercentage.toFixed(2),

            // State information
            pharmacyStateCode: pharmacyStateCode || "",
            patientStateCode: patientStateCode || "",
            isSameState, // Include isSameState in payload

            returnedItems: items
        };

        setLoading(true);
        try {
            // Use your return sales API endpoint
            const response = await returnSalesAPI(payload);
            const data = response.data?.data?.returnedSales;
            const receiptQrData = response.data?.data.paymentQR;

            toast.success(response.data?.message || t("Return processed successfully"));

            // Create converted items with tax information for invoice
            const billDiscountRate =
                totals.subtotal > 0 ? (totals.discount || 0) / totals.subtotal : 0;

            const convertedItems = cart
                .filter(item => (item.returnQuantity || 0) > 0)
                .map(item => {
                    const itemKey = `${item._id}-${item.selectedBatchId}`;
                    const taxInfo = itemTaxes[itemKey] || {};
                    const proportion = (item.returnQuantity || 0) / (item.quantity || 1);

                    // Determine item-specific tax percentages based on isSameState
                    let itemCGSTPercentage = 0;
                    let itemSGSTPercentage = 0;
                    let itemIGSTPercentage = 0;

                    const taxableBeforeDiscount =
                        (taxInfo.taxableAmount || 0) * (item.returnQuantity > 0 ? proportion : 0);
                    const taxableAfterDiscount =
                        taxableBeforeDiscount - taxableBeforeDiscount * billDiscountRate;

                    const taxRate = taxInfo.taxPercentage || 0;

                    let itemTax = 0;
                    let itemCGST = 0;
                    let itemSGST = 0;
                    let itemIGST = 0;
                    let itemVAT = 0;

                    if (posTaxType === "gst") {
                        if (!isSameState) {
                            itemTax = (taxableAfterDiscount * taxRate) / 100;
                            itemIGST = itemTax;
                            itemIGSTPercentage = taxRate;
                        } else {
                            itemTax = (taxableAfterDiscount * taxRate) / 100;
                            itemCGST = itemTax / 2;
                            itemSGST = itemTax / 2;
                            itemCGSTPercentage = taxRate / 2;
                            itemSGSTPercentage = taxRate / 2;
                        }
                    } else if (posTaxType === "vat") {
                        itemVAT = (taxableAfterDiscount * taxRate) / 100;
                        itemTax = itemVAT;
                    }

                    return {
                        ...item,
                        salePrice: convertCurrency(item.salePrice, currency, paymentData.currency),
                        quantity: item.returnQuantity || 0,
                        // Add tax information to each item
                        tax: convertCurrency(itemTax, currency, paymentData.currency),
                        taxPercentage: taxInfo.taxPercentage || 0,
                        CGST: convertCurrency(itemCGST, currency, paymentData.currency),
                        SGST: convertCurrency(itemSGST, currency, paymentData.currency),
                        IGST: convertCurrency(itemIGST, currency, paymentData.currency),
                        VAT: convertCurrency(itemVAT, currency, paymentData.currency),
                        CGSTPercentage: itemCGSTPercentage,
                        SGSTPercentage: itemSGSTPercentage,
                        IGSTPercentage: itemIGSTPercentage,
                        isIGST: !isSameState && posTaxType === "gst"
                    };
                });

            // Calculate totals for invoice display (converted)
            const convertedTax = convertCurrency(totals.tax, currency, paymentData.currency);
            const convertedCGST = convertCurrency(totals.CGST, currency, paymentData.currency);
            const convertedSGST = convertCurrency(totals.SGST, currency, paymentData.currency);
            const convertedIGST = convertCurrency(totals.IGST, currency, paymentData.currency);
            const convertedVAT = convertCurrency(totals.VAT, currency, paymentData.currency);
            const convertedSubtotal = convertCurrency(totals.subtotal, currency, paymentData.currency);
            const convertedDiscount = convertCurrency(totals.discount, currency, paymentData.currency);
            const convertedNetTotal = convertCurrency(totals.netTotal, currency, paymentData.currency);

            // Calculate tax percentages for invoice display based on isSameState
            let invoiceCGSTPercentage = 0;
            let invoiceSGSTPercentage = 0;
            let invoiceIGSTPercentage = 0;

            if (posTaxType === "gst") {
                if (isSameState) {
                    invoiceCGSTPercentage = maxTaxPercentage / 2;
                    invoiceSGSTPercentage = maxTaxPercentage / 2;
                } else {
                    invoiceIGSTPercentage = maxTaxPercentage;
                }
            }

            const invoice = {
                date: new Date().toISOString(),
                pharmacy: pharmacyPOS?.facility_name,
                email: pharmacyPOS?.email,
                phone: `${pharmacyPOS?.contact_person?.phone_code || ""} ${pharmacyPOS?.contact_person?.mobile || ""}`.trim(),
                customer: paymentData.patientName || t("Walk-in Customer"),
                items: convertedItems,

                // Use converted values
                subtotal: convertedSubtotal,
                discount: convertedDiscount,
                tax: convertedTax,
                CGST: convertedCGST,
                SGST: convertedSGST,
                IGST: convertedIGST,
                VAT: convertedVAT,
                total: Number(convertedNetTotal),
                paid: Number(paymentData.amount),

                // Tax metadata - UPDATED with isSameState
                taxType: posTaxType.toUpperCase(),
                taxPercentage: maxTaxPercentage,
                CGSTPercentage: invoiceCGSTPercentage,
                SGSTPercentage: invoiceSGSTPercentage,
                IGSTPercentage: invoiceIGSTPercentage,

                // Tax type indicator
                isSameState,
                isIGST: !isSameState && posTaxType === "gst",

                invoiceNo: data?.salesNumber || `RET${Date.now()}`,

                // Currency info
                currency: paymentData.currency,
                currencySymbol: getCurrencySymbol(paymentData.currency),

                discountMeta: { type: discountType, value: discountValue },
                receiptQrData,

                // State info for display
                pharmacyStateCode,
                patientStateCode,

                // Payment info
                paymentType: paymentData.type,

                // Return specific info
                reason: paymentData.reason,

                // Tax summary based on state
                taxSummary: {
                    isSameState,
                    taxType: posTaxType,
                    intraStateTax: isSameState ? "CGST + SGST" : "N/A",
                    interStateTax: !isSameState ? "IGST" : "N/A",
                    pharmacyState: pharmacyStateCode || "Not set",
                    patientState: patientStateCode || "Not set"
                }
            };

            setInvoiceData(invoice);
            setShowInvoice(true);
            setShowPaymentModal(false);
            return invoice;
        } catch (error) {
            console.error("Error processing return:", error);
            toast.warning(
                error.response?.data?.message || t("Failed to process return. Please try again later.")
            );
            return null;
        } finally {
            setLoading(false);
        }
    }, [
        cart,
        itemTaxes,
        customerType,
        discountType,
        discountValue,
        getCurrencySymbol,
        paymentData,
        pharmacyPOS,
        setLoading,
        t,
        transportCost,
        totals,
        convertCurrency,
        currency,
        parentSalesId,
        exchangeRate,
        posTaxType,
        pharmacyStateCode,
        patientStateCode,
        selectedPatient,
        isSameState // Add isSameState to dependencies
    ]);

    const closeInvoice = useCallback(() => {
        setShowInvoice(false);
    }, []);

    return {
        paymentData,
        setPaymentData,
        showPaymentModal,
        openPaymentModal,
        closePaymentModal,
        handlePaymentSave,
        errors,
        showInvoice,
        invoiceData,
        closeInvoice,
        resetPaymentState,
        totals,
        isSameState,
    };
};